import classNames from 'classnames';

import { FilterSelect } from '@/components/shared/FilterSelect/FilterSelect';
import { TextField } from '@/components/shared/TextField/TextField';

import { SUPPORTED_LANGUAGES } from '../../../../Content/const';
import { JOURNEY_BRANDS } from '../../../const';
import { count } from '../../../count';
import { JourneyDraft, UpdateDraft } from '../../../draft';
import { DescribedIssue } from '../../../issues';
import { Selection } from '../../../selection';
import IssueList from '../../IssueList/IssueList';
import Field from '../Form/Field';
import Section from '../Form/Section';
import PanelHeader from '../PanelHeader/PanelHeader';

interface JourneyInfoPanelProps {
  draft: JourneyDraft;
  update: UpdateDraft;
  // Undefined until the journey is first saved: the backend works the issues out.
  issues?: DescribedIssue[];
  isDirty: boolean;
  onSelect: (selection: Selection) => void;
}

const JourneyInfoPanel = ({ draft, update, issues, isDirty, onSelect }: JourneyInfoPanelProps) => {
  const toggleBrand = (brand: string) =>
    update((d) => {
      d.brands = d.brands.includes(brand)
        ? d.brands.filter((b) => b !== brand)
        : [...d.brands, brand];
    });

  const renderIssues = () => {
    if (!issues) {
      return (
        <p className="text-sm text-neutral-50">
          Save the journey to see what’s left before it can go online.
        </p>
      );
    }

    if (!issues.length) {
      return (
        <div className="rounded-xl bg-success-10 px-4 py-3 text-sm font-semibold text-success-60">
          ✓ Everything is in place — the journey can go online.
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-danger-20 bg-danger-10/40 p-3">
        <p className="px-2 mb-2 text-sm font-semibold text-danger-60">
          {count(issues.length, 'thing')} to fix before this journey can go online
        </p>
        <IssueList issues={issues} onSelect={onSelect} />
      </div>
    );
  };

  return (
    <div className="grid gap-6">
      <PanelHeader eyebrow="Journey" title={draft.title || 'Untitled journey'} />

      <Section title="General Info">
        <Field label="Title">
          <TextField
            className="!w-full"
            inputClassName="!font-normal"
            value={draft.title}
            placeholder="e.g. Attachment Styles"
            onChange={(e) => update((d) => void (d.title = e.target.value))}
          />
        </Field>
        <Field
          label="Cover Image (URL)"
          hint="Shown on the journeys page and in the journey switcher"
        >
          <div className="flex gap-3 items-center">
            {draft.imageUrl ? (
              <img
                src={draft.imageUrl}
                alt=""
                className="w-10 h-10 rounded-lg object-cover bg-white-100 shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white-100 border border-neutral-20 shrink-0" />
            )}
            <TextField
              className="!w-full"
              inputClassName="!font-normal"
              value={draft.imageUrl}
              placeholder="https://example.com/image.png"
              onChange={(e) => update((d) => void (d.imageUrl = e.target.value))}
            />
          </div>
        </Field>
        <Field label="Short description" hint="One line, wherever the journey is offered">
          <TextField
            className="!w-full"
            inputClassName="!font-normal"
            value={draft.shortDescription}
            placeholder="Understand the push-and-pull in your relationship..."
            onChange={(e) => update((d) => void (d.shortDescription = e.target.value))}
          />
        </Field>
        <Field label="Overview" hint="What the reader will get from the whole journey">
          <TextField
            isTextArea
            rows={4}
            className="!w-full"
            inputClassName="!font-normal"
            value={draft.overview}
            placeholder="Type here..."
            onChange={(e) => update((d) => void (d.overview = e.target.value))}
          />
        </Field>
      </Section>

      <Section title="Settings">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Personalization" hint="The tag it’s offered for">
            <TextField
              className="!w-full"
              inputClassName="!font-normal"
              value={draft.personalization}
              placeholder="e.g. get-ex-back"
              onChange={(e) => update((d) => void (d.personalization = e.target.value.trim()))}
            />
          </Field>
          <Field label="Colour" hint="The journey’s tone in the app">
            <div className="flex gap-3 items-center">
              <span
                className="w-10 h-10 rounded-lg border border-neutral-20 shrink-0"
                style={{ backgroundColor: draft.color }}
              />
              <TextField
                className="!w-full"
                inputClassName="!font-normal"
                value={draft.color}
                placeholder="#843DFF"
                onChange={(e) => update((d) => void (d.color = e.target.value.trim()))}
              />
            </div>
          </Field>
        </div>
        <div className="flex gap-8 items-start">
          <Field label="Language">
            <FilterSelect
              value={draft.language}
              onChange={(language) => update((d) => void (d.language = language))}
              options={SUPPORTED_LANGUAGES.map(({ value, label, icon }) => ({
                value,
                label,
                icon,
              }))}
            />
          </Field>
          <Field label="Brands" hint="Who can start it">
            <div className="flex gap-2">
              {JOURNEY_BRANDS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleBrand(value)}
                  className={classNames('px-3 py-2 rounded-lg border text-sm font-semibold', {
                    'border-primary-50 bg-primary-10 text-primary-60': draft.brands.includes(value),
                    'border-neutral-20 bg-white-100 text-neutral-60': !draft.brands.includes(value),
                  })}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Section>

      <div className="grid gap-3">
        <h3 className="text-lg font-semibold text-neutral-90">
          Before publishing
          {issues && isDirty && (
            <span className="ml-2 text-xs font-normal text-neutral-50">as of the last save</span>
          )}
        </h3>
        {renderIssues()}
      </div>
    </div>
  );
};

export default JourneyInfoPanel;
